// Coordenador de paginação de hotel — contenção da rajada de refine (#2242).
//
// O hook de páginas (`useHotelOfferPages`) não tinha coordenação entre requisições contra o
// mesmo `sessionId`: suas travas são refs POR-INSTÂNCIA, então N painéis abertos
// no mesmo sessionId disparavam N refines, e uma instância cujo `requestKey`
// oscilava disparava M concorrentes sem cancelar as anteriores. Este módulo é a
// peça compartilhada (module-level) que colapsa e cancela por CHAVE.
//
// POLÍTICA (verbatim, é a especificação — casos DISJUNTOS, ambos necessários):
//   - Chave IDÊNTICA (sessionId, page, cuts, order) colapsa na requisição em voo.
//   - Chave NOVA dispara JÁ e aborta a velha (quando ninguém mais a quer).
//   - PROIBIDO serializar chaves diferentes por sessionId — k0 e k1 correm
//     concorrentes enquanto ambas têm interessados; nada espera.
//
// Puro: não faz IO nem conhece o host — recebe a função de fetch e um signal.

type PageRun<T> = (signal: AbortSignal) => Promise<T>;

type Entry = {
  readonly promise: Promise<unknown>;
  readonly controller: AbortController;
  refs: number;
};

const inFlight = new Map<string, Entry>();

/** Comparação ordinal, não `localeCompare`: a chave tem de ser independente de locale
 *  e de versão do ICU. Mesma ordem que o `.sort()` sem comparador já produzia —
 *  comparação de string em JS é ordem de code unit UTF-16. */
const byCodeUnit = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** Serialização ESTÁVEL (requisito de borda 4): dois `cuts` logicamente iguais
 *  construídos em ordens de campo diferentes têm de gerar a MESMA chave, senão
 *  o colapso não acontece e o coordenador vira decoração silenciosa. */
function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort(byCodeUnit);
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(record[k])}`).join(',')}}`;
}

/** A identidade da requisição AO FORNECEDOR — os mesmos campos que viajam no
 *  `fetchPage` do host. `resetToken`/`blockOffers` NÃO entram: são gatilho de
 *  re-fetch no hook, não parte do que se pergunta ao fornecedor. */
export function hotelPageCoalesceKey(parts: {
  sessionId: string;
  page: number;
  cuts?: unknown;
  order?: string;
}): string {
  return stableStringify({
    sessionId: parts.sessionId,
    page: parts.page,
    cuts: parts.cuts ?? null,
    order: parts.order ?? null,
  });
}

export type AcquiredPage<T> = {
  /** A promise COMPARTILHADA da requisição em voo. É read-only por contrato
   *  (requisito de borda 5): a mesma resposta vai para N consumidores; nenhum
   *  a muta — o hook copia (`[...offers]`) na borda ao aplicar no estado. */
  readonly promise: Promise<T>;
  /** Solta esta aquisição. Idempotente por chamador (requisito de borda 3):
   *  chamar duas vezes é no-op, e o abort só dispara quando o ÚLTIMO
   *  interessado solta. */
  readonly release: () => void;
};

/**
 * Anexa-se à requisição em voo da `key`, ou dispara uma nova. Devolve a promise
 * compartilhada e um `release` idempotente.
 */
export function acquireHotelPage<T>(key: string, run: PageRun<T>): AcquiredPage<T> {
  let entry = inFlight.get(key);
  if (!entry) {
    const controller = new AbortController();
    const promise = run(controller.signal);
    const created: Entry = { promise, controller, refs: 0 };
    // O settle limpa a entrada — a MESMA chave, depois, dispara uma requisição
    // nova (a resposta anterior já é passado). `=== created` evita apagar uma
    // entrada mais nova que reusou a chave.
    const clear = (): void => {
      if (inFlight.get(key) === created) inFlight.delete(key);
    };
    // Requisito de borda 2: a promise compartilhada NUNCA fica sem handler. Se
    // ela abortar com zero awaiters vivos (todos churnaram), isto impede um
    // `unhandledrejection` global. Não rouba a rejeição de quem faz `await`: é
    // uma cadeia `.catch` própria, os consumidores anexam as suas.
    promise.then(clear, clear);
    promise.catch(() => {});
    entry = created;
    inFlight.set(key, created);
  }
  entry.refs += 1;
  const held = entry;
  let released = false;
  return {
    promise: held.promise as Promise<T>,
    release: () => {
      if (released) return;
      released = true;
      // O `released` acima já garante que cada chamador decrementa no máximo uma
      // vez, então `refs` não iria a negativo pelo uso normal. O `Math.max(0, …)`
      // é cinto para os testes que dão `resetHotelPageCoordinatorForTests()` com
      // aquisições ainda vivas: o release tardio dessas não deve cavar abaixo de 0.
      held.refs = Math.max(0, held.refs - 1);
      // Aborta só quando ninguém mais quer ESTA requisição, e só se ela ainda é
      // a corrente da chave (não uma que já settou nem uma nova que reusou a
      // chave). É o "aborta a velha" da política — nunca mata quem tem refs.
      if (held.refs === 0 && inFlight.get(key) === held) {
        held.controller.abort();
        inFlight.delete(key);
      }
    },
  };
}

/** Só para testes: zera o estado compartilhado entre casos. */
export function resetHotelPageCoordinatorForTests(): void {
  inFlight.clear();
}
